import { AnonymousSurveyFollowUpModel, ConsentSignatureResultModel, FileResultModel, SurveyResultModel } from "../../types/SurveyResult";
import { getRequestHeaders } from "../getRequestHeaders";
import { makeUrl } from "../makeUrl";
import { postRequest } from "../requests";
import { handleResponse } from "../response";

export function persistConsentResult(projectID: string, surveyResult: SurveyResultModel, participantIdentifier?: string) {
    return persistSurveyResultToUrl(surveyResult,
        `api/researchkit/studyenrollment/studies/${projectID}/consent?identifier=${participantIdentifier || ''}`,
        `api/researchkit/persistence/TaskResultFile/${surveyResult.TaskRunUUID}`, false);
}

export function persistLinkConsentResult(linkKey: string, surveyResult: SurveyResultModel) {
    return persistSurveyResultToUrl(surveyResult,
        `api/researchkit/studyenrollment/consent/${linkKey}`,
        `api/researchkit/persistence/TaskResultFile/${surveyResult.TaskRunUUID}`, false);
}

export function persistCoordinatorSurveyResult(surveyResult: SurveyResultModel, surveyId: string, participantId: string) {
    return persistSurveyResultToUrl(surveyResult,
        `api/researchkit/administration/coordinatorenteredsurveys/${surveyId}?participantID=${participantId}`,
        `api/researchkit/administration/coordinatorenteredsurveys/TaskResultFile/${surveyResult.TaskRunUUID}?participantID=${participantId}`, false);
}

export function persistCoordinatorIntakeSurveyResult(projectId: string, surveyResult: SurveyResultModel, surveyId: string) {
    return persistSurveyResultToUrl(surveyResult,
        `api/researchkit/administration/projects/${projectId}/coordinatorintakesurveys/${surveyId}`,
        `api/researchkit/administration/projects/${projectId}/coordinatorintakesurveys/TaskResultFile/${surveyResult.TaskRunUUID}`, false);
}

export function persistSurveyResult(surveyResult: SurveyResultModel) {
    return persistSurveyResultToUrl(surveyResult,
        'api/researchkit/persistence/TaskResults',
        `api/researchkit/persistence/TaskResultFile/${surveyResult.TaskRunUUID}`, true);
}

export function persistLinkSurveyResult(linkKey: string, surveyResult: SurveyResultModel) {
    return persistSurveyResultToUrl(surveyResult,
        `api/researchkit/surveylink/${linkKey}/TaskResults`,
        `api/researchkit/surveylink/${linkKey}/TaskResultFile/${surveyResult.TaskRunUUID}`, true);
}

function handleSavingDisplay(show: boolean) {
	if (document.getElementById("saving-overlay")) document.getElementById("saving-overlay")!.style.display = show ? 'block' : 'none';
	if (document.getElementById("saving-modal")) document.getElementById("saving-modal")!.style.display = show ? 'block' : 'none';
}

export async function persistAnonymousSurveyResult(linkIdentifier: string, surveyResult: SurveyResultModel) {
    if (window.renderTurnstile) {
		handleSavingDisplay(true);
    }

	let headers = getRequestHeaders();
	if (window.renderTurnstile) {
		try {
			const token = await (window as any).CareEv?.WaitForClientVerificationToken();
			if (!token) {
				throw new Error("Failed to obtain client verification token.");
			}
			headers.append("clientverificationtoken", token);
			headers.append("clientverificationtype", "turnstile");
		} finally {
			handleSavingDisplay(false);
		}
	}
	let fileExtractionResult = extractFilesFromSurveyResult(surveyResult);
	let path = `api/researchkit/anonymoussurveys/link/${linkIdentifier}/TaskResults`;
	var result = await fetch(makeUrl(path), {
		method: 'POST',
		headers: headers,
		body: JSON.stringify([fileExtractionResult.surveyResultWithoutFiles])
	}).then(response => handleResponse(response)) as AnonymousSurveyFollowUpModel;
	//the original link identifier may be a project code if this is a recruitment survey
	//have to update to the link identifier of the real participant to upload files
	var finalLinkIdentifier = result.LinkIdentifier;
	return Promise.all(fileExtractionResult.files.map(file => persistSurveyFileToUrl(file, `api/researchkit/anonymoussurveys/link/${finalLinkIdentifier}/TaskResultFile/${surveyResult.TaskRunUUID}`)))
		.then(() => result);
}

async function persistSurveyResultToUrl(surveyResult: SurveyResultModel, surveyPersistUrl: string, filePersistUrl: string, embedInArray: boolean = false) {
    let fileExtractionResult = extractFilesFromSurveyResult(surveyResult);
    let result = await postRequest(surveyPersistUrl, embedInArray ? [fileExtractionResult.surveyResultWithoutFiles] : fileExtractionResult.surveyResultWithoutFiles);
    return Promise.all(fileExtractionResult.files.map(file => persistSurveyFileToUrl(file, filePersistUrl)))
        .then(() => result);
}

async function persistSurveyFileToUrl(file: SurveyFileToUpload, url: string) {
    let delimeter = url.includes("?") ? "&" : "?";
    let finalUrl = url + `${delimeter}fileStorageLocation=${file.fileStorageLocation}`;
    var formData = new FormData();
    formData.append("file", file.file);
    let headers = getRequestHeaders();
    headers.delete('Content-Type');
    return fetch(makeUrl(finalUrl), {
        method: 'POST',
        headers: headers,
        body: formData
    }).then(response => handleResponse(response));
}

interface FileExtractionResult {
    surveyResultWithoutFiles: SurveyResultModel;
    files: SurveyFileToUpload[];
}

interface SurveyFileToUpload {
    fileStorageLocation: "LocalData" | "UploadedFiles";
    file: File;
}

function extractFilesFromSurveyResult(surveyResult: SurveyResultModel): FileExtractionResult {
    let files: SurveyFileToUpload[] = [];

    //get the unmodified file names first, so those do not change
    let fileNames = surveyResult.StepResults.map(stepResult => {
        return stepResult.QuestionResults.filter(result => result.Type === "FileResult").map(result => {
            let fileResult = result as FileResultModel;
            if (fileResult.FileName && !fileResult.Modified) {
                return fileResult.FileName;
            }
            return undefined;
        });
    }).flat().filter(name => name !== undefined) as string[];

    let stepResults = surveyResult.StepResults.map(stepResult => {
        let questionResults = stepResult.QuestionResults.map(result => {
            if (result.Type === "FileResult") {
                let fileResult = result as FileResultModel;

                if (!fileResult.Modified) {
                    fileNames.push(fileResult.FileName!);
                }

                if (fileResult.File && fileResult.FileName) {
                    let fileName = fileResult.FileName;
                    let targetFileName = fileName;

                    //Ensure uploaded files are uniquely named across the survey
                    let index = 1;
                    while (fileNames.includes(targetFileName)) {
                        let fileExtension = fileName.split('.').pop();
                        let fileNameWithoutExtension = fileName.substring(0, fileName.length - (fileExtension?.length || 0) - 1);
                        targetFileName = `${fileNameWithoutExtension} (${index}).${fileExtension}`;
                        index++;
                    }
                    fileNames.push(targetFileName);

                    let file = fileResult.File;
                    if (targetFileName !== fileName) {
                        file = new File([file], targetFileName, { type: file.type });
                    }

                    files.push({
                        file: file,
                        fileStorageLocation: fileResult.StorageLocation || "LocalData"
                    });
                    return { ...fileResult, File: undefined, FileName: targetFileName };
                }
            }

            if (result.Type === "ConsentSignatureResult") {
                const consentSignatureResult = result as ConsentSignatureResultModel;
                //don't upload the base 64 image, just remove it from the result
                return { ...consentSignatureResult, Signature: { ...consentSignatureResult.Signature, HandwrittenSignatureBase64: undefined } };
            }

            return result;
        });
        return { ...stepResult, QuestionResults: questionResults };
    });
    return {
        surveyResultWithoutFiles: { ...surveyResult, StepResults: stepResults },
        files: files
    };
}